import type { Idea } from '@/lib/types';
import { GrowthIcon } from './GrowthIcon';
import { ProgressDots } from './ProgressDots';
import { getProgressDots, calculateDaysSinceWatered, getTimeCue, isNearWither } from '@/lib/time-utils';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';

interface IdeaCardProps {
  idea: Idea;
  onClick?: () => void;
  showFocusMuting?: boolean; // When true, non-focused ideas appear muted
}

export function IdeaCard({ idea, onClick, showFocusMuting = false }: IdeaCardProps) {
  const dots = getProgressDots(idea);
  const isWithered = idea.status === 'withered';
  const isTree = idea.growth_stage === 'tree';
  const nearWither = isNearWither(idea);
  const timeCue = getTimeCue(idea);
  const isFocused = idea.is_focus;
  const isMuted = showFocusMuting && !isFocused;

  return (
    <Card
      className={cn(
        'cursor-pointer group',
        'transition-all duration-300 ease-out',
        'hover:translate-y-[-2px]',
        isFocused && 'ring-2 ring-primary/40 shadow-md',
        isMuted && 'opacity-50',
        isTree 
          ? 'tree-card' 
          : 'idea-card-living',
        isWithered && 'withered-card',
        nearWither && 'near-wither'
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className={cn(
              "font-medium text-sm truncate",
              isTree ? "text-muted-foreground" : "text-foreground"
            )}>
              {idea.title || 'Untitled Idea'}
            </h3>
            <p className="text-sm text-muted-foreground/70 mt-1 line-clamp-2 leading-relaxed">
              {idea.one_liner || idea.raw_input}
            </p>
            {/* Human time cue or muted status */}
            <p className={cn(
              "text-[11px] mt-2 font-light",
              nearWither 
                ? "text-amber-600/60 dark:text-amber-400/50" 
                : "text-muted-foreground/50"
            )}>
              {isMuted ? 'Not in focus.' : timeCue}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            {isFocused && (
              <span className="text-[10px] text-primary/70 font-medium uppercase tracking-wide">
                Focus
              </span>
            )}
            <GrowthIcon stage={idea.growth_stage} className="flex-shrink-0 opacity-70" />
          </div>
        </div>
        
        {/* Trees: no progress section */}
        {!isTree && !isWithered && (
          <div className="mt-3 flex items-center justify-between">
            <ProgressDots count={dots} />
          </div>
        )}
        
        {/* Withered: quiet prompt */}
        {isWithered && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground/40 italic font-light">
              This idea has gone quiet.
            </span>
            <span className="text-[11px] text-primary/60 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200">
              Revive
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
